import React, { useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Input } from 'ncoded-component-library';
import CheckToggle from 'components/CheckToggle';
import useMatchMedia from 'hooks/useMatchMedia';
import { AGREEMENT_LIMITS, AgreementField } from 'models/Agreement';
import {
  AGREEMENT_FIELD_LABEL_KEYS,
  getHasCustomLabel,
  getIsBuyerField,
  getIsSignatureField,
} from 'agreementUtils';
import FieldEditorContext from '../../providers/FieldEditor/FieldEditor.context';

import './FieldSettings.styles.scss';
import './FieldSettings.styles.responsive.scss';

type FieldSettingsProps = {
  className?: string;
  field: AgreementField;
  style?: React.CSSProperties;
};

const stopPropagation = (event: React.SyntheticEvent) =>
  event.stopPropagation();

const FieldSettings: React.FC<FieldSettingsProps> = (props) => {
  const { className, field, style } = props;

  const { updateField, removeField } = useContext(FieldEditorContext);

  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const typeLabel = t(AGREEMENT_FIELD_LABEL_KEYS[field.type]);

  const classes = classNames('field-settings', className);

  return (
    <div
      className={classes}
      style={style}
      onPointerDown={stopPropagation}
      onClick={stopPropagation}
    >
      <p className="field-settings__title">{typeLabel}</p>

      {getHasCustomLabel(field) && (
        <label className="field-settings__label">
          <span>{t('label')}</span>
          <Input
            value={field.label || ''}
            placeholder={typeLabel}
            maxLength={AGREEMENT_LIMITS.FIELD_LABEL_MAX_LENGTH}
            autoFocus={!field.label && !isPhablet}
            onChange={(e) => updateField({ ...field, label: e.target.value })}
          />
        </label>
      )}

      {getIsBuyerField(field) ? (
        <label className="field-settings__required">
          <span>{t('required')}</span>
          <CheckToggle
            checked={field.required}
            onChange={(e) =>
              updateField({ ...field, required: e.target.checked })
            }
          />
        </label>
      ) : (
        <p className="field-settings__note">
          {t(
            getIsSignatureField(field)
              ? 'agreementFieldAlwaysRequired'
              : 'agreementFieldsAutoFilled',
          )}
        </p>
      )}

      <Button
        type="button"
        variant="link"
        className="field-settings__delete"
        onClick={() => removeField(field.id)}
      >
        {t('agreementDeleteField')}
      </Button>
    </div>
  );
};

export default FieldSettings;
